// Motion for the kit (design system: Motion, ADR-109). Only transform and opacity move, 150–250 ms; with
// prefers-reduced-motion every change is instant. Nothing waits for an animation to finish.
import { animate } from 'motion/mini';

export const reducedMotion = () =>
  typeof window === 'undefined' ||
  typeof window.matchMedia !== 'function' ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const canAnimate = (el: Element | null | undefined): el is HTMLElement =>
  !!el && typeof (el as HTMLElement).animate === 'function' && !reducedMotion();

const isRtl = (el: Element) => (el.closest('[dir]')?.getAttribute('dir') ?? document.documentElement.dir) === 'rtl';

/** A page entering: a card from the inline end, back to a list from the inline start, a top-level page fades. */
export function pageEnter(el: HTMLElement | null, from: 'end' | 'start' | 'fade') {
  if (!canAnimate(el)) return;
  if (from === 'fade') {
    void animate(el, { opacity: [0, 1] }, { duration: 0.16, ease: 'easeOut' });
    return;
  }
  // Inline end is the right in LTR and the left in RTL.
  const sign = (from === 'end') !== isRtl(el) ? 1 : -1;
  const done = animate(
    el,
    { opacity: [0, 1], transform: [`translateX(${sign * 24}px)`, 'translateX(0px)'] },
    { duration: 0.22, ease: 'easeOut' },
  );
  // Leave no transform behind: a transformed ancestor would capture the sheets' fixed layer.
  void done.then(() => {
    el.style.transform = '';
    el.style.opacity = '';
  });
}

/** A line added to a list slides in. */
export function lineEnter(el: HTMLElement | null) {
  if (!canAnimate(el)) return;
  void animate(el, { opacity: [0, 1], transform: ['translateY(-8px)', 'translateY(0px)'] }, { duration: 0.18 }).then(
    () => {
      el.style.transform = '';
      el.style.opacity = '';
    },
  );
}

/** A figure that changed after an action pulses once. */
export function pulse(el: HTMLElement | null) {
  if (!canAnimate(el)) return;
  void animate(el, { transform: ['scale(1)', 'scale(1.06)', 'scale(1)'] }, { duration: 0.4 }).then(() => {
    el.style.transform = '';
  });
}

/** Moves an element from where it was (a previous box) to where it is now. */
export function slideFrom(el: HTMLElement | null, dx: number, dw: number, onDone: () => void) {
  if (!canAnimate(el) || (dx === 0 && dw === 1)) {
    onDone();
    return;
  }
  el.style.transformOrigin = '0 0';
  void animate(
    el,
    { transform: [`translateX(${dx}px) scaleX(${dw})`, 'translateX(0px) scaleX(1)'] },
    {
      duration: 0.2,
      ease: 'easeOut',
    },
  ).then(onDone, onDone);
}
